import { ChangeEvent, Dispatch, useEffect, useReducer } from 'react';
import { SelectChangeEvent } from '@mui/material/Select';
import dayjs from 'dayjs';

import { CustomInput, CustomSelect, MultipleSelect, Text } from '../../../shared/uiComponents';
import { validateName } from '../../../shared/Helpers/functions';
import { useIsTelehealth, useTreatmentPlanningOptions } from '../hook';
import {
	ACTIONS,
	ActionProperties,
	ERRORS,
	initialTreatmentPlaningErrorState,
	treatmentPlaningErrorReducer,
} from './treatmentPlanningReducer';
import { Behaviors, SignatureInput, Targets, TelehealthLocations, TimeInfo } from '../components';
import { useSoapNoteFieldDisabled } from '../helpers';

import { useSelector } from '../../../redux/store';
import { UpdateTreatmentPlanningProperties } from '../../../redux/API/ClientAPIHelpers/soapNoteProperties';

const TreatmentPlanningData = ({
	data,
	setData,
	save,
}: {
	data: UpdateTreatmentPlanningProperties;
	save: () => void;
	setData: Dispatch<ActionProperties>;
}) => {
	const today = new Date();
	const todayString = dayjs(today).format('MM/DD/YYYY');

	const { participantsList, treatmentUpdatesList } = useTreatmentPlanningOptions();

	const isTelehealth = useIsTelehealth(data.locationId);

	const { getFieldDisabled } = useSoapNoteFieldDisabled();

	const [errors, setErrors] = useReducer(treatmentPlaningErrorReducer, initialTreatmentPlaningErrorState);

	const locationList = useSelector((state) => state.soapNote.locations);
	const errorList = useSelector((state) => state.soapNote.error?.errors);

	useEffect(() => {
		if (data.participantResponses.length) return;
		const payload = participantsList.filter((x) => x.isDefault).map((y) => y.id);
		setData({ type: ACTIONS.setParticipants, payload });
	}, [participantsList, setData, data]);

	useEffect(() => {
		if (!errorList) return;
		const list = Object.keys(errorList);
		list.forEach((id) => setErrors({ type: ERRORS[id], payload: true }));
	}, [errorList]);

	const participantsHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({ type: ACTIONS.setParticipants, payload: value });
		if (!errors.ParticipantResponses) return;
		setErrors({ payload: false, type: ERRORS.ParticipantResponses });
	};

	const locationHandler = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setLocationId });
		if (!errors.LocationId) return;
		setErrors({ payload: false, type: ERRORS.LocationId });
	};

	const masteredGoalsAndObjectivesHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ type: ACTIONS.setMasteredGoalsAndObjectives, payload: value });
		if (!errors.MasteredGoalsAndObjectives) return;
		setErrors({ payload: false, type: ERRORS.MasteredGoalsAndObjectives });
	};

	const newGoalsAndObjectivesHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ type: ACTIONS.setNewGoalsAndObjectives, payload: value });
		if (!errors.NewGoalsAndObjectives) return;
		setErrors({ payload: false, type: ERRORS.NewGoalsAndObjectives });
	};

	const treatmentUpdateResponsesHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({ type: ACTIONS.setTreatmentUpdateResponses, payload: value });
		if (!errors.TreatmentUpdateResponses) return;
		setErrors({ payload: false, type: ERRORS.TreatmentUpdateResponses });
	};

	const analysisOfGoalsHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ type: ACTIONS.setAnalysisOfGoals, payload: value });
		if (!errors.AnalysisOfGoals) return;
		setErrors({ payload: false, type: ERRORS.AnalysisOfGoals });
	};

	const goalUpdatesHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ type: ACTIONS.setGoalUpdates, payload: value });
		if (!errors.GoalUpdates) return;
		setErrors({ payload: false, type: ERRORS.GoalUpdates });
	};

	const coordinationAndMaterialsPreppedHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({
			type: ACTIONS.setCoordinationAndMaterialsPrepped,
			payload: value,
		});
		if (!errors.CoordinationAndMaterialsPrepped) return;
		setErrors({ payload: false, type: ERRORS.CoordinationAndMaterialsPrepped });
	};

	const descriptionOfSessionHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({
			type: ACTIONS.setDescriptionOfSession,
			payload: value,
		});
		if (!errors.DescriptionOfSession) return;
		setErrors({ payload: false, type: ERRORS.DescriptionOfSession });
	};

	const signatureHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({
			type: ACTIONS.setSignature,
			payload: value.toUpperCase(),
		});
		if (!errors.Signature) return;
		setErrors({ payload: false, type: ERRORS.Signature });
	};

	const validateSignatureHandler = () => {
		if (validateName(data.signature)) {
			save();

			return;
		}
		setErrors({ payload: true, type: ERRORS.Signature });
	};

	const participantsOtherHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setParticipantsOther });
		if (!errors.TreatmentPlanningParticipantOther) return;
		setErrors({
			payload: false,
			type: ERRORS.TreatmentPlanningParticipantOther,
		});
	};

	const isOtherSelectedForParticipants = data.participantResponses.find(
		(x) => participantsList.find((y) => y.id === x)?.text === 'Other',
	);

	const treatmentUpdateOtherHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setTreatmentUpdateOther });
		if (!errors.TreatmentUpdateOther) return;
		setErrors({ payload: false, type: ERRORS.TreatmentUpdateOther });
	};

	const isOtherSelectedForTreatmentUpdate = data.treatmentUpdateResponses.find(
		(x) => treatmentUpdatesList.find((y) => y.id === x)?.text === 'Other',
	);

	return (
		<div>
			<TimeInfo disabled={getFieldDisabled('startTime')} save={save} />
			<CustomSelect
				label="Location:"
				data={locationList}
				value={data.locationId ? data.locationId : ''}
				setValue={locationHandler}
				className="marginBottom16"
				error={errors.LocationId}
				disabled={getFieldDisabled('locationId')}
				onBlur={save}
			/>
			{isTelehealth && (
				<TelehealthLocations
					setData={setData}
					data={data}
					disabled={getFieldDisabled('clientTelehealthLocationId')}
					save={save}
				/>
			)}
			<MultipleSelect
				label="Session Participants:"
				data={participantsList}
				initialValue={data.participantResponses}
				setValue={participantsHandler}
				error={errors.ParticipantResponses}
				className="marginBottom16"
				disabled={getFieldDisabled('participantResponses')}
				onBlur={save}
			/>
			{isOtherSelectedForParticipants && (
				<CustomInput
					label="If other, please describe: "
					value={data.treatmentPlanningParticipantOther}
					onChange={participantsOtherHandler}
					className="marginBottom16"
					multiline
					disabled={getFieldDisabled('treatmentPlanningParticipantOther')}
					onBlur={save}
					error={errors.TreatmentPlanningParticipantOther}
				/>
			)}
			<CustomInput
				label="Mastered goals and objectives:"
				value={data.masteredGoalsAndObjectives}
				onChange={masteredGoalsAndObjectivesHandler}
				error={errors.MasteredGoalsAndObjectives}
				multiline
				className="marginBottom16"
				disabled={getFieldDisabled('masteredGoalsAndObjectives')}
				onBlur={save}
			/>
			<CustomInput
				label="New goals and objectives:"
				value={data.newGoalsAndObjectives}
				onChange={newGoalsAndObjectivesHandler}
				multiline
				error={errors.NewGoalsAndObjectives}
				className="marginBottom16"
				disabled={getFieldDisabled('newGoalsAndObjectives')}
				onBlur={save}
			/>
			<MultipleSelect
				label="Updates to Treatment:"
				data={treatmentUpdatesList}
				initialValue={data.treatmentUpdateResponses}
				setValue={treatmentUpdateResponsesHandler}
				error={errors.TreatmentUpdateResponses}
				className="marginBottom16"
				disabled={getFieldDisabled('treatmentUpdateResponses')}
				onBlur={save}
			/>
			{isOtherSelectedForTreatmentUpdate && (
				<CustomInput
					label="If other, please describe: "
					value={data.treatmentUpdateOther}
					onChange={treatmentUpdateOtherHandler}
					className="marginBottom16"
					multiline
					disabled={getFieldDisabled('treatmentUpdateOther')}
					onBlur={save}
					error={errors.TreatmentUpdateOther}
				/>
			)}
			<CustomInput
				label="Analysis of goals reviewed:"
				value={data.analysisOfGoals}
				onChange={analysisOfGoalsHandler}
				multiline
				error={errors.AnalysisOfGoals}
				className="marginBottom16"
				disabled={getFieldDisabled('analysisOfGoals')}
				onBlur={save}
			/>
			<CustomInput
				label="Updates to goals:"
				value={data.goalUpdates}
				onChange={goalUpdatesHandler}
				multiline
				className="marginBottom16"
				error={errors.GoalUpdates}
				disabled={getFieldDisabled('goalUpdates')}
				onBlur={save}
			/>
			<CustomInput
				label="Coordination and materials prepped:"
				value={data.coordinationAndMaterialsPrepped}
				onChange={coordinationAndMaterialsPreppedHandler}
				multiline
				rowCount={3}
				error={errors.CoordinationAndMaterialsPrepped}
				className="marginBottom16"
				disabled={getFieldDisabled('coordinationAndMaterialsPrepped')}
				onBlur={save}
			/>
			<Text title="Session Details" type="h4" font="header" fontWeight="semiBold" className="marginBottom16 title" />
			<Targets />
			<Behaviors />
			<CustomInput
				label="Description of Summary: "
				value={data.descriptionOfSession}
				onChange={descriptionOfSessionHandler}
				error={errors.DescriptionOfSession}
				className="marginBottom8"
				multiline
				rowCount={3}
				disabled={getFieldDisabled('descriptionOfSession')}
				onBlur={save}
			/>
			<SignatureInput
				value={data.signature}
				setValue={signatureHandler}
				error={errors.Signature}
				disabled={getFieldDisabled('signature')}
				onBlur={validateSignatureHandler}
			/>
			<div>
				<Text title="Date of signature:" type="psm" fontWeight="semiBold" />
				<Text title={todayString} type="psm" />
			</div>
		</div>
	);
};

export default TreatmentPlanningData;
